import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

interface SmartGoalProgressProps {
  goal: {
    id: string;
    description: string;
    measurement_criteria: string;
    target_value: number;
    current_value: number;
  };
  onUpdate: () => void;
}

function SmartGoalProgress({ goal, onUpdate }: SmartGoalProgressProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newValue, setNewValue] = useState(goal.current_value.toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const numValue = parseFloat(newValue);
      
      if (isNaN(numValue)) {
        throw new Error('Please enter a valid number');
      }

      const { error: updateError } = await supabase
        .from('smart_goals')
        .update({ current_value: numValue })
        .eq('id', goal.id);

      if (updateError) throw updateError;

      setIsEditing(false);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-b pb-4">
      <h3 className="text-gray-900 mb-1">{goal.description}</h3>
      <p className="text-sm text-gray-500 mb-2">{goal.measurement_criteria}</p>
      <div className="flex items-center mb-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2">
          <div
            className="bg-gray-900 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(100, (goal.current_value / goal.target_value) * 100)}%`,
            }}
          />
        </div>
        <span className="ml-4 text-sm text-gray-500">
          {goal.current_value} / {goal.target_value}
        </span>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="mt-2">
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className="block w-24 rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 text-sm"
              step="any"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className={`px-3 py-1 text-sm rounded-md ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 hover:bg-gray-800'
              } text-white`}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setError(null);
                setNewValue(goal.current_value.toString());
              }}
              disabled={loading}
              className="px-3 py-1 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </form>
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="mt-2 text-sm text-gray-900 hover:text-gray-600 transition-colors"
        >
          Update Progress
        </button>
      )}
    </div>
  );
}

export default SmartGoalProgress;